import { useEffect, useState } from "react"
import { Box, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import { Spinner } from "@ui/spinner/Spinner"
import { GD } from "src/GD"
import { hasIssuerReviewPending, issuerReviewDelay, isIssuerReviewStatus } from "src/utils/issuerReview"
import { getIssuerKycState, type IssuerKycPath } from "src/utils/issuerKyc"
import { S_UNIVERSAL_DIALOG } from "./universalDialog/signals"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { getIssuerDisplayName } from "../issuer/issuerIdentity"
import { DS } from "../ds/Kit"
import { KycCallout, KycStepper } from "./ProviderKycStatus"

const TITLE = "Details submitted for review"

export const IssuerReviewWait = ({ issuer, issuerLabel, review, kind, onClose }: {
	issuer: number
	issuerLabel?: string
	review?: IKycReview
	kind?: IssuerKycPath
	onClose: () => void
}) => {
	const [currentReview, setCurrentReview] = useState(review)
	const issuerContext = useOptionalIssuer()
	const contextIssuer = issuerContext?.issuers?.find(item => item.issuer === issuer)
	const [fetchedLabel, setFetchedLabel] = useState<string>()
	const displayName = getIssuerDisplayName({ issuer, issuer_label: fetchedLabel || issuerLabel || contextIssuer?.issuer_label, name: contextIssuer?.name })
	useEffect(() => {
		let active = true
		let nextReview = review
		let hasReviewMetadata = Boolean(review)
		let timer: ReturnType<typeof setTimeout>
		setCurrentReview(review)
		setFetchedLabel(undefined)
		const schedule = () => { timer = setTimeout(() => void poll(), issuerReviewDelay(nextReview)) }
		const poll = async () => {
			try {
				const response = await GD.REQ_KYC_ISSUERS.request({ issuer })
				if (!active) return
				if (!response.error) {
					const selected = response.data?.find(item => item.issuer === issuer)
					// A missing issuer or a failed response does not prove a verdict.
					if (selected) {
						setFetchedLabel(selected.issuer_label || selected.name)
						const path = kind ? getIssuerKycState(response.data, issuer, kind) : null
						const pending = path
							? path.stage === "issuer" && (path.status === "pending" || path.status === "under_review")
							: hasIssuerReviewPending(selected, "approved")
						if (path && !pending) {
							onClose()
							return
						}
						if (!selected.review && (hasReviewMetadata || !pending)) {
							onClose()
							return
						}
						nextReview = selected.review
						hasReviewMetadata ||= Boolean(selected.review)
						setCurrentReview(selected.review)
					}
				}
			} catch {
				// Temporary network failures keep the wait screen and its last cadence.
			}
			if (active) schedule()
		}
		schedule()
		return () => { active = false; clearTimeout(timer) }
	}, [issuer, kind, review, onClose])

	const eta = currentReview?.eta_text?.trim()
	return (
		<VBox gap='20px' sx={{ alignItems: "center", textAlign: "center", color: DS.text }}>
			<Box sx={{ width: "100%" }}><KycStepper current={2} /></Box>
			<Box
				role='status'
				aria-label='Waiting for issuer review'
				sx={{ width: "88px", height: "88px", borderRadius: "50%", display: "grid", placeItems: "center", background: "radial-gradient(circle, #FFFFFF 38%, #E8F0F8 39%, #E8F0F8 62%, #F6F7FB 63%)", boxShadow: "0 0 0 1px #E0E3FB" }}
			>
				<Spinner size={40} color={DS.accent} />
			</Box>
			<VBox gap='6px' sx={{ alignItems: "center" }}>
				<Typo sx={{ fontSize: "20px", fontWeight: 700, color: DS.ink, letterSpacing: "-0.02em", lineHeight: 1.25 }}>{`${displayName} is reviewing your details`}</Typo>
				<Typo sx={{ fontSize: "14px", lineHeight: 1.5, color: DS.muted }}>
					You can close this window. We will update the status automatically.
				</Typo>
			</VBox>
			<VBox gap='10px' sx={{ width: "100%", textAlign: "left" }}>
				<KycCallout tone='approved' title='Your details were saved successfully and sent to the issuer.' />
				<VBox gap='4px' sx={{ padding: "14px 18px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.soft }}>
					<Typo sx={{ fontSize: "12px", color: DS.muted }}>{eta ? "Estimated review time" : "Review status"}</Typo>
					<Typo sx={{ fontSize: "15px", fontWeight: 600, color: DS.ink, lineHeight: 1.5 }}>{eta || "Your details are being reviewed by the issuer."}</Typo>
				</VBox>
			</VBox>
			<Button title='Close' variant='secondary' onClick={onClose} sx={{ minHeight: "44px", minWidth: "120px", borderRadius: "12px" }} />
		</VBox>
	)
}

let reviewSequence = 0
export const openIssuerReviewWait = (issuer: number, review?: IKycReview, kind?: IssuerKycPath, issuerLabel?: string) => {
	const close = () => S_UNIVERSAL_DIALOG.invoke(null)
	S_UNIVERSAL_DIALOG.replace({
		label: TITLE,
		size: "small",
		hideHistoryBack: true,
		content: <IssuerReviewWait key={++reviewSequence} issuer={issuer} issuerLabel={issuerLabel} review={review} kind={kind} onClose={close} />,
		closeDialog: close
	})
}

/** Restore issuer review on app entry without changing the own-KYC access gate. */
export const IssuerReviewRestore = () => {
	const issuer = useOptionalIssuer()?.selectedIssuerId
	useEffect(() => {
		if (!issuer) return
		let active = true
		void GD.REQ_KYC_STATUS.request({ issuer }).then(response => {
			if (!active || response.error || S_UNIVERSAL_DIALOG.hasActiveDialog()) return
			const data = response.data
			if (data.issuer !== undefined && data.issuer !== issuer) return
			if (isIssuerReviewStatus(data)) {
				openIssuerReviewWait(issuer, data.review, undefined, data.issuer_label)
			}
		}).catch(() => { /* A failed status read must not block the application. */ })
		return () => { active = false }
	}, [issuer])
	return null
}
